import { useState, useCallback, useMemo } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Copy, Check, Trash2, Dice5, Settings, ChevronDown, ChevronUp
} from 'lucide-react'

type NumberType = 'integer' | 'decimal' | 'hex' | 'binary'

function generateRandomNumbers(
  type: NumberType,
  min: number,
  max: number,
  count: number,
  unique: boolean,
  sort: 'none' | 'asc' | 'desc'
): string[] {
  const results: string[] = []
  const seen = new Set<string>()
  let attempts = 0
  const maxAttempts = count * 100

  while (results.length < count && attempts < maxAttempts) {
    attempts++
    let value: number

    if (type === 'integer') {
      value = Math.floor(Math.random() * (max - min + 1)) + min
    } else if (type === 'decimal') {
      value = Math.random() * (max - min) + min
    } else if (type === 'hex') {
      value = Math.floor(Math.random() * (max - min + 1)) + min
    } else {
      value = Math.floor(Math.random() * (max - min + 1)) + min
    }

    let str: string
    if (type === 'hex') {
      str = '0x' + Math.abs(value).toString(16).toUpperCase()
    } else if (type === 'binary') {
      str = '0b' + Math.abs(value).toString(2)
    } else if (type === 'decimal') {
      str = value.toFixed(2)
    } else {
      str = String(value)
    }

    if (unique && seen.has(str)) continue
    seen.add(str)
    results.push(str)
  }

  if (sort === 'asc') {
    results.sort((a, b) => {
      if (type === 'hex') return parseInt(a.slice(2), 16) - parseInt(b.slice(2), 16)
      if (type === 'binary') return parseInt(a.slice(2), 2) - parseInt(b.slice(2), 2)
      return parseFloat(a) - parseFloat(b)
    })
  } else if (sort === 'desc') {
    results.sort((a, b) => {
      if (type === 'hex') return parseInt(b.slice(2), 16) - parseInt(a.slice(2), 16)
      if (type === 'binary') return parseInt(b.slice(2), 2) - parseInt(a.slice(2), 2)
      return parseFloat(b) - parseFloat(a)
    })
  }

  return results
}

function App() {
  const { t } = useI18n()
  const [type, setType] = useState<NumberType>('integer')
  const [min, setMin] = useState('1')
  const [max, setMax] = useState('100')
  const [count, setCount] = useState('10')
  const [unique, setUnique] = useState(false)
  const [sort, setSort] = useState<'none' | 'asc' | 'desc'>('none')
  const [showSettings, setShowSettings] = useState(false)
  const [results, setResults] = useState<string[]>([])
  const [copied, setCopied] = useState(false)
  const [history, setHistory] = useState<{ params: string; results: string[] }[]>([])

  const minVal = parseFloat(min)
  const maxVal = parseFloat(max)
  const countVal = parseInt(count)

  const isValid = !isNaN(minVal) && !isNaN(maxVal) && !isNaN(countVal) && maxVal >= minVal && countVal > 0 && countVal <= 10000

  const generate = useCallback(() => {
    if (!isValid) return
    const newResults = generateRandomNumbers(type, minVal, maxVal, countVal, unique, sort)
    setResults(newResults)
    setHistory(prev => [{ params: `${type} ${min}-${max} ×${count}`, results: newResults }, ...prev].slice(0, 5))
  }, [type, minVal, maxVal, countVal, unique, sort, isValid])

  const copyAll = useCallback(async () => {
    if (results.length === 0) return
    try {
      await navigator.clipboard.writeText(results.join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }, [results])

  const clear = useCallback(() => {
    setResults([])
    setHistory([])
  }, [])

  const typeLabel = useMemo(() => {
    switch (type) {
      case 'integer': return t('randomNumber.integer') || 'Integer'
      case 'decimal': return t('randomNumber.decimal') || 'Decimal'
      case 'hex': return t('randomNumber.hex') || 'Hex'
      case 'binary': return t('randomNumber.binary') || 'Binary'
    }
  }, [type, t])

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('randomNumber.title') || 'Random Number Generator'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('randomNumber.subtitle') || 'Generate random numbers. Integer, decimal, hex, binary. '}
          <span className="text-primary font-medium">{t('randomNumber.privacy') || '100% client-side'}</span>.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Config Panel */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-4">
          {/* Type */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-2">{t('randomNumber.type') || 'Type'}</label>
            <div className="flex flex-wrap gap-2">
              {(['integer', 'decimal', 'hex', 'binary'] as NumberType[]).map(nt => (
                <button
                  key={nt}
                  onClick={() => setType(nt)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    type === nt ? 'bg-brand-primary text-white' : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {t(`randomNumber.${nt}`) || nt}
                </button>
              ))}
            </div>
          </div>

          {/* Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">{t('randomNumber.min') || 'Min'}</label>
              <input
                type="number"
                value={min}
                onChange={e => setMin(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">{t('randomNumber.max') || 'Max'}</label>
              <input
                type="number"
                value={max}
                onChange={e => setMax(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
          </div>

          {/* Count */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">{t('randomNumber.count') || 'Count'} ({t('randomNumber.maxCount') || 'max 10,000'})</label>
            <input
              type="number"
              value={count}
              onChange={e => setCount(e.target.value)}
              min={1}
              max={10000}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/50"
            />
          </div>

          {/* Settings */}
          <div>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-lg text-sm text-foreground hover:bg-muted/80 transition-colors"
            >
              <Settings className="w-4 h-4" />
              {t('randomNumber.settings') || 'Settings'}
              {showSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showSettings && (
              <div className="mt-3 bg-background rounded-lg border border-border p-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unique}
                    onChange={e => setUnique(e.target.checked)}
                    className="w-4 h-4 rounded border-border text-brand-primary"
                  />
                  <span className="text-sm text-foreground">{t('randomNumber.unique') || 'Unique values only'}</span>
                </label>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">{t('randomNumber.sort') || 'Sort'}</label>
                  <div className="flex flex-wrap gap-2">
                    {(['none', 'asc', 'desc'] as const).map(s => (
                      <button
                        key={s}
                        onClick={() => setSort(s)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          sort === s ? 'bg-brand-primary text-white' : 'bg-muted text-foreground hover:bg-muted/80'
                        }`}
                      >
                          {t(`randomNumber.${s}`) || s}
                        </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {!isValid && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
              {t('randomNumber.invalid') || 'Please enter valid numbers. Max must be ≥ min, count must be 1-10,000.'}
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={generate}
            disabled={!isValid}
            className="w-full py-3 bg-brand-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            <Dice5 className="w-5 h-5" />
            {t('randomNumber.generate') || 'Generate'}
          </button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('randomNumber.results') || 'Results'} ({results.length})
                </h3>
                <span className="text-xs text-muted-foreground">{typeLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyAll}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-foreground hover:bg-muted transition-colors"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? (t('actions.copied') || 'Copied!') : (t('actions.copy') || 'Copy')}
                </button>
                <button
                  onClick={clear}
                  className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Results Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {results.map((num, idx) => (
                <div
                  key={idx}
                  className="px-3 py-2 bg-background border border-border rounded-lg text-center text-sm font-mono text-foreground hover:border-brand-primary transition-colors cursor-pointer"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(num)
                    } catch {}
                  }}
                  title={t('actions.copy') || 'Click to copy'}
                >
                  {num}
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2 border-t border-border">
              <span>{t('randomNumber.sum') || 'Sum'}: <strong className="text-foreground">{
                results.reduce((sum, r) => sum + (type === 'hex' ? parseInt(r.slice(2), 16) : type === 'binary' ? parseInt(r.slice(2), 2) : parseFloat(r)), 0).toLocaleString()
              }</strong></span>
              <span>{t('randomNumber.avg') || 'Avg'}: <strong className="text-foreground">{
                (results.reduce((sum, r) => sum + (type === 'hex' ? parseInt(r.slice(2), 16) : type === 'binary' ? parseInt(r.slice(2), 2) : parseFloat(r)), 0) / results.length).toFixed(2)
              }</strong></span>
              <span>{t('randomNumber.minResult') || 'Min'}: <strong className="text-foreground">{
                Math.min(...results.map(r => type === 'hex' ? parseInt(r.slice(2), 16) : type === 'binary' ? parseInt(r.slice(2), 2) : parseFloat(r))).toLocaleString()
              }</strong></span>
              <span>{t('randomNumber.maxResult') || 'Max'}: <strong className="text-foreground">{
                Math.max(...results.map(r => type === 'hex' ? parseInt(r.slice(2), 16) : type === 'binary' ? parseInt(r.slice(2), 2) : parseFloat(r))).toLocaleString()
              }</strong></span>
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 1 && (
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              {t('randomNumber.history') || 'History'}
            </h3>
            <div className="space-y-2">
              {history.slice(1).map((h, idx) => (
                <button
                  key={idx}
                  onClick={() => setResults(h.results)}
                  className="w-full text-left px-3 py-2 bg-background border border-border rounded-lg hover:border-brand-primary transition-colors"
                >
                  <div className="text-xs text-muted-foreground">{h.params}</div>
                  <div className="text-sm font-mono text-foreground truncate">{h.results.slice(0, 5).join(', ')}{h.results.length > 5 ? '...' : ''}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">{t('security.note')}</p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
